import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Paper,
  Button,
  Stack,
  Box,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  collection,
  query,
  where,
  doc,
  updateDoc,
  Timestamp,
   onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "./firebase";
import { useNavigate } from "react-router-dom";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { getDisplayStatus } from "./utils/statusLabels";
import UploadPreview, { appendPickedFiles } from "./components/UploadPreview";
import { showBuiltIn } from "./utils/accountBuiltIns";

const FRAMING_CHECKLIST_ITEMS = [
  { key: "color", label: "تأكدت من لون الإطار" },
  { key: "type", label: "تأكدت من نوع الإطار" },
  { key: "size", label: "تأكدت من قياس الإطار" },
  { key: "angle", label: "تأكدت من زاوية الإطار" },
  { key: "hanger", label: "تأكدت من مكان التعليكة" },
  { key: "finalMeasurements", label: "اخذت قياسات اللوحة النهائية بعد التنفيذ - مطابقة" },
];

const BurhanDashboard = () => {
  // ✅ إخفاء الطلبات عن واجهة الورشة - من صلاحية بنين/المدير فقط (طلب
  // المستخدم). الإخفاء بصري فقط بالداشبورد - الطلب يبقى يمشي بنفس الفلو
  // تماماً (نفس الحالة/الأزرار/الـhistory)، فقط ينتقل لقسم "الطلبات المخفية"
  // بالأسفل بدل القائمة الرئيسية.
  const currentUsername = localStorage.getItem("username");
  const canManageHidden =
    currentUsername === "Baneen@123" || currentUsername === "admin";

  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [problemNote, setProblemNote] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [framingDialogOrder, setFramingDialogOrder] = useState(null);
  const [selectedFramingVideos, setSelectedFramingVideos] = useState([]);
  const [framingUploading, setFramingUploading] = useState(false);
  const [framingChecklist, setFramingChecklist] = useState({
    color: false,
    type: false,
    size: false,
    angle: false,
    hanger: false,
    finalMeasurements: false,
  });
  const [packagingDialogOrder, setPackagingDialogOrder] = useState(null);
  const [selectedPackagingVideos, setSelectedPackagingVideos] = useState([]);
  const [packagingUploading, setPackagingUploading] = useState(false);

// ✅ تبديل إخفاء/إظهار طلب (بنين/المدير فقط - الزر نفسه محمي بـcanManageHidden
// بالواجهة). orders مبني على onSnapshot بالأعلى فتنعكس تلقائياً بدون حاجة
// لتحديث محلي يدوي هنا.
const toggleHiddenFromBoard = async (order) => {
  const hide = !order.hiddenFromBoard;
  const orderRef = doc(db, "orders", order.id);
  await updateDoc(orderRef, {
    hiddenFromBoard: hide,
    hiddenBy: hide ? currentUsername : null,
    hiddenAt: hide ? Timestamp.now() : null,
  });
};

const handleSendProblem = async () => {
  await apiFetch(`${API_BASE}/orders/${selectedOrder.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      status: "مشكلة - عند برهان",
      step: "مشكلة",
    }),
  });

  // تحديث الواجهة
  setOrders(prev =>
    prev.map(o =>
      o.id === selectedOrder.id
        ? {
            ...o,
            status: "مشكلة - عند برهان",
            completedSteps: [...(o.completedSteps || []), "مشكلة"]
          }
        : o
    )
  );

  setOpenDialog(false);
};
const sendToBackend = async (id, step) => {
  try {
    console.log("🚀 STEP:", step); // 👈 أضف هذا

    await apiFetch(`${API_BASE}/orders/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        status: `عند برهان - ${step}`,
        step: step
      }),
    });

    console.log("تم التحديث من الباك اند");

  } catch (error) {
    console.error("خطأ:", error);
  }
};

const handleDelivery = async (orderId, type) => {
  try {
    await apiFetch(`${API_BASE}/orders/${orderId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        deliveryType: type,
        deliveryPaid: false,
        deliveryPaidBy: null,
      }),
    });

    // ✅ هذا أهم جزء
    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? {
              ...o,
              deliveryType: type,
              deliveryPaid: false,
              deliveryPaidBy: null,
            }
          : o
      )
    );

    // ✅ بطلب المستخدم: تسجيل الدفع لم يعد يصير من الورشة إطلاقاً (صار حصراً
    // عبر نظام الحسابات عند ربط قيد قبض بالطلب) - الفلو يكمل تلقائياً هنا
    // لكل الطلبات بدون انتظار أي تأكيد دفع، تماماً متل ما كان يصير سابقاً
    // فقط لحسابي "حساب استاذ مروان"/"ماركتنك".
    await continueDeliveryFlow(orderId, type);

  } catch (error) {
    console.error("خطأ التوصيل:", error);
  }
};


const handleStep = async (order, step) => {
  // نخلي updateStep يحسب كلشي
  const orderDoc = orders.find((o) => o.id === order.id);

  let updatedSteps = [...(orderDoc.completedSteps || [])];
  let newStatus = "عند برهان";

  // 🧠 نفس لوجكك القديم
  if (step !== "رسم" && step !== "مشكلة") {
    updatedSteps.push(step);
  }

  if (step === "مشكلة") {
    newStatus = "عند يوسف";
  }

  if (step === "إطار" && !orderDoc.withDrawing) {
    newStatus = "عند برهان - بانتظار المقارنة";
  }

  // 👇 هنا نرسل للباك
  await apiFetch(`${API_BASE}/orders/${order.id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      status: newStatus,
      step: step
    }),
  });
};





  const isReadyForDelivery = (order) => {
  const packagingDone = order.completedSteps?.includes("تغليف");
  const installationDone = order.completedSteps?.includes("تركيب");
  return packagingDone && (!order.withAssembly || installationDone);
};
  const isBurhanStage = (status) => {
  if (!status) return false;
  return (
    status === "عند برهان" ||
    status.startsWith("عند برهان -") ||   // يشمل "رسم حليمة" و "مكتمل"
    status === "feedbackRequired" ||       // إذا تريد تكمل من عنده
    status === "بانتظار تصوير التركيب"    // حسب منطقك
  );
};
const getPriorityInfo = (priority) => {
  if (!priority) {
    return { label: "غير محدد", bg: "#9e9e9e", dot: "#e0e0e0" };
  }

  if (priority === "High")
    return { label: "عالي", bg: "#c62828", dot: "#ff5252" };

  if (priority === "Medium")
    return { label: "متوسط", bg: "#f9a825", dot: "#ffe082" };

  if (priority === "Low")
    return { label: "منخفض", bg: "#2e7d32", dot: "#69f0ae" };

  return { label: priority, bg: "#607d8b", dot: "#cfd8dc" };
};

const getNextAllowedStep = (order) => {
  const completed = order.completedSteps || [];
  // ⛔ إذا بانتظار المقارنة → لا يسمح بأي خطوة
if (order.status === "عند برهان - بانتظار المقارنة") {
  return null;
}
    // ✅ إذا تمت الموافقة على المقارنة → يسمح بتغليف
 if (order.status === "عند برهان - تمت الموافقة على المقارنة") {
  return "تغليف";
}

  const steps = getStepsForOrder(order).filter((s) => s !== "مشكلة");

  const isDoneLocal = (step) => {
    if (step === "رسم") return isDrawingCompleted(order);
    return completed.includes(step);
  };

  // 1) إذا رسم انرسل ولسه مو مكتمل: وقف كلشي
  if (
    order.withDrawing &&
    order.drawingSent &&
    !isDrawingCompleted(order) &&
    (order.status === "عند برهان - رسم حليمة" ||
      order.status === "عند برهان - رسم حليمة بدأت")
  ) {
    return "رسم";
  }

  // 2) امشِ بالخطوات إلى أن تصل تغليف
  const nextNormal = steps.find((s) => !isDoneLocal(s));
  if (nextNormal && nextNormal !== "تركيب") {
    // قبل التغليف طبيعي (طباعة/إطار/رسم/تغليف)
    // تركيب نعالجه وحده تحت
    return nextNormal;
  }

  // 3) بعد ما يخلص تغليف:
 if (completed.includes("تغليف")) {
  if (!order.deliveryType) return "تغليف";
  if (!order.deliveryConfirmed) return "تغليف";

  if (order.withAssembly && !completed.includes("تركيب")) {
    return "تركيب";
  }

  // ❌ قديم
  // return "مشكلة";

  // ✅ جديد
  return null;
}

  // 4) إذا الخطوة الجاية هي تركيب (قبل التغليف لا تصير أصلاً)
  if (nextNormal) return nextNormal;

  return "مشكلة";
};




  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
const navigate = useNavigate();

  useEffect(() => {
  const q = query(
    collection(db, "orders"),
    where("status", ">=", "عند برهان"),
    where("status", "<=", "عند برهان\uffff")
  );

  const unsub = onSnapshot(q, (querySnapshot) => {
    const data = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const sorted = data.sort(
      (a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0)
    );
    setOrders(sorted);
  });

  return () => unsub();
}, []);


const isDirectSaleOrder = (order) => order?.isDirectSale === true;

const getStepsForOrder = (order) => {
  // ✅ بيع مباشر: بدون طباعة وبدون إطار وبدون يوسف
  if (isDirectSaleOrder(order)) {
    const steps = [];

    // إذا بيه رسم: برهان لازم يرسل رسم لحليمة
    if (order.withDrawing) steps.push("رسم");

    // يبدأ بتغليف مباشرة
    steps.push("تغليف");

    // التوصيل والدفع عندك منفصلين (deliveryType / markDeliveryPaid)
    // فمو لازم نحط "شركة التوصيل" كخطوة هنا
    // بس إذا انت تريدها كخطوة، نضيفها (اختياري):
    // steps.push("شركة التوصيل");

    if (order.withAssembly) steps.push("تركيب");

    steps.push("مشكلة");
    return steps;
  }

  // ✅ "مطبعة خارجية": نفس الفلو القديم قبل إضافة واجهة المطبعة - "طباعة" تضل خطوة يدوية عند الورشة
  // ✅ "مطبعة الشركة": الطباعة صارت واجهة منفصلة قبل الورشة (الطلب يوصل مطبوع خلص)
  const steps = order.printChoice === "external" ? ["طباعة", "إطار"] : ["إطار"];
  if (order.withDrawing) steps.push("رسم");
  steps.push("تغليف");
  if (order.withAssembly) steps.push("تركيب");
  steps.push("مشكلة");
  return steps;
};

const isDrawingCompleted = (order) =>
  order?.status === "عند برهان - رسم حليمة مكتمل";

const isStepDone = (order, step) => {
  if (step === "رسم") return isDrawingCompleted(order);
  return order.completedSteps?.includes(step);
};



const updateStep = async (orderId, step) => {
  const orderRef = doc(db, "orders", orderId);
  const orderDoc = orders.find((o) => o.id === orderId);
  const newHistory = [
    ...(orderDoc.history || []),
    {
      status: step,
      by: "برهان",
      timestamp: Timestamp.now(),
      ...(step === "مشكلة" && problemNote ? { note: problemNote } : {}),
    },
  ];

  let updatedSteps = [...(orderDoc.completedSteps || [])];
  let newStatus = "عند برهان";

  // ✅ برهان يضغط "رسم"
  if (step === "رسم") {
    try {
      const drawingRes = await apiFetch(`${API_BASE}/orders/${orderId}/send-to-drawing`, {
        method: "POST",
      });
      if (!drawingRes.ok) {
        throw new Error(await drawingRes.text().catch(() => ""));
      }
    } catch (err) {
      console.error("فشل في نقل بيانات الرسام:", err);
      alert("❌ حدث خطأ أثناء نقل بيانات الرسم إلى الرسام");
      return;
    }

    // ✅ إشعار واجهة حليمة (الرسامات) - بعد نجاح النقل (كان سابقاً بين كتابة
    // halimaRequests وتحديث orders، أصبح الآن بعد اكتمال العملية الذرية
    // بالكامل - لا يؤثر على شيء لأن فشل الإشعار كان دائماً غير موقِف للفلو)
    try {
      await fetch("http://localhost:8080/send-to-user", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          uid: "halima@123",
          title: "🎨 طلب جديد من الورشة",
          body: `تم وصول طلب جديد من الورشة إلى قسم الرسم.`,
        }),
      });
      console.log("✅ إشعار أُرسل إلى الرسام بنجاح!");
    } catch (error) {
      console.error("❌ فشل إرسال الإشعار إلى الرسام:", error);
    }

    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? {
              ...order,
              drawingSent: true,
              status: "عند برهان - رسم حليمة",
              history: newHistory,
            }
          : order
      )
    );

    return; // ⛔ يوقف هنا
  } else {
    updatedSteps.push(step);
      // ✅ إذا ضغط "إطار" والطلب ما بي رسم
  if (step === "إطار" && !orderDoc.withDrawing) {
    const frameRes = await apiFetch(`${API_BASE}/orders/${orderId}/frame-no-drawing`, {
      method: "PUT",
    });
    if (!frameRes.ok) {
      console.error("خطأ أثناء إكمال خطوة الإطار:", await frameRes.text().catch(() => ""));
      return;
    }

    setOrders((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? {
              ...o,
              completedSteps: updatedSteps,
status: "عند برهان - بانتظار المقارنة",
              history: newHistory,
            }
          : o
      )
    );

    return; // ⛔ يوقف هنا لحد ما بنين توافق
  }

  }

 if (step === "مشكلة") {
  newStatus = "عند يوسف";

  // ✅ تصفير البروسيس حتى يرجع كأنه طلب جديد لمن يرجع لبرهان
  const problemRes = await apiFetch(`${API_BASE}/orders/${orderId}/report-problem`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...(problemNote ? { reason: problemNote } : {}) }),
  });
  if (!problemRes.ok) {
    console.error("خطأ أثناء إرسال المشكلة:", await problemRes.text().catch(() => ""));
    return;
  }

  setOrders((prev) =>
    prev.map((o) =>
      o.id === orderId
        ? {
            ...o,
            status: newStatus,
            completedSteps: [],
            deliveryType: null,
            deliveryPaid: false,
            deliveryPaidBy: null,
            deliveryPaidAt: null,
            deliveryPaidVisibleAt: null,
            deliveryConfirmed: false,
            deliveryConfirmedAt: null,
            drawingSent: false,
            history: newHistory,
          }
        : o
    )
  );

  setOpenDialog(false);
  setProblemNote("");
  return;
}
 else {
    const allSteps = getStepsForOrder(orderDoc);
    const isAllDone = allSteps
      .filter((s) => s !== "مشكلة" && s !== "رسم")
      .every((s) => updatedSteps.includes(s));
// 🟡 تركيب: مرحلة وسط بانتظار تصوير
if (step === "تركيب") {
  const updatedWithInstall = [...updatedSteps];
  if (!updatedWithInstall.includes("تركيب")) updatedWithInstall.push("تركيب");

  await updateDoc(orderRef, {
    completedSteps: updatedWithInstall,
    status: "بانتظار تصوير التركيب",
    feedbackRequired: false, // ✅ بعده مو feedback، الموظفات بانتظار تصوير
    history: newHistory,
  });

  setOrders((prev) =>
    prev.map((o) =>
      o.id === orderId
        ? {
            ...o,
            completedSteps: updatedWithInstall,
            status: "بانتظار تصوير التركيب",
            feedbackRequired: false,
            history: newHistory,
          }
        : o
    )
  );

  return;
}


// ✅ ماكو بعد شي اسمه "شركة التوصيل" كـ step
// التوصيل والدفع صاروا عبر deliveryType + markDeliveryPaid
if (step !== "رسم") {
  newStatus = "عند برهان";
}

  }

  const stepRes = await apiFetch(`${API_BASE}/orders/${orderId}/step`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ step }),
  });
  if (!stepRes.ok) {
    console.error("خطأ أثناء تحديث الخطوة:", await stepRes.text().catch(() => ""));
    return;
  }


  setOrders((prev) =>
    prev.map((order) =>
      order.id === orderId
        ? {
            ...order,
            completedSteps: updatedSteps,
            status: newStatus,
            history: newHistory,
          }
        : order
    )
  );

  setOpenDialog(false);
  setProblemNote("");
};

// 🎥 رفع فيديو/فيديوهات التأطير (إلزامي قبل إكمال خطوة "إطار")
const handleUploadFramingVideos = async () => {
  if (!framingDialogOrder) return;

  if (!selectedFramingVideos.length) {
    alert("⚠️ يجب رفع فيديو واحد على الأقل لعملية التأطير قبل المتابعة");
    return;
  }

  const missingChecklistItem = FRAMING_CHECKLIST_ITEMS.find(
    (item) => !framingChecklist[item.key]
  );
  if (missingChecklistItem) {
    alert(`⚠️ يجب تأكيد كل نقاط الفحص قبل المتابعة (${missingChecklistItem.label})`);
    return;
  }

  try {
    setFramingUploading(true);

    const uploadedUrls = [];
    for (const file of selectedFramingVideos) {
      const videoRef = ref(
        storage,
        `framingVideos/${framingDialogOrder.id}/${Date.now()}-${file.name}`
      );
      await uploadBytes(videoRef, file);
      const url = await getDownloadURL(videoRef);
      uploadedUrls.push(url);
    }

    const updatedLinks = [
      ...(framingDialogOrder.framingVideoLinks || []),
      ...uploadedUrls,
    ];

    const res = await apiFetch(
      `${API_BASE}/orders/${framingDialogOrder.id}/frame`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ framingVideoLinks: updatedLinks, framingChecklist }),
      }
    );
    if (!res.ok) throw new Error(await res.text().catch(() => ""));

    setOrders((prev) =>
      prev.map((o) =>
        o.id === framingDialogOrder.id
          ? { ...o, framingVideoLinks: updatedLinks }
          : o
      )
    );

    const orderId = framingDialogOrder.id;

    setSelectedFramingVideos([]);
    setFramingDialogOrder(null);
    setFramingUploading(false);

    // ✅ نكمل نفس بروسس زر "إطار" بعد ما يخلص رفع الفيديو
    await updateStep(orderId, "إطار");
  } catch (err) {
    console.error(err);
    alert("❌ حدث خطأ أثناء رفع فيديو التأطير");
    setFramingUploading(false);
  }
};

// 🎥 رفع فيديو/فيديوهات التغليف (إلزامي قبل إكمال خطوة "تغليف")
const handleUploadPackagingVideos = async () => {
  if (!packagingDialogOrder) return;

  if (!selectedPackagingVideos.length) {
    alert("⚠️ يجب رفع فيديو واحد على الأقل لعملية التغليف قبل المتابعة");
    return;
  }

  try {
    setPackagingUploading(true);

    const uploadedUrls = [];
    for (const file of selectedPackagingVideos) {
      const videoRef = ref(
        storage,
        `packagingVideos/${packagingDialogOrder.id}/${Date.now()}-${file.name}`
      );
      await uploadBytes(videoRef, file);
      const url = await getDownloadURL(videoRef);
      uploadedUrls.push(url);
    }

    const updatedLinks = [
      ...(packagingDialogOrder.packagingVideoLinks || []),
      ...uploadedUrls,
    ];

    const res = await apiFetch(
      `${API_BASE}/orders/${packagingDialogOrder.id}/packaging`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packagingVideoLinks: updatedLinks }),
      }
    );
    if (!res.ok) throw new Error(await res.text().catch(() => ""));

    setOrders((prev) =>
      prev.map((o) =>
        o.id === packagingDialogOrder.id
          ? { ...o, packagingVideoLinks: updatedLinks }
          : o
      )
    );

    const orderId = packagingDialogOrder.id;

    setSelectedPackagingVideos([]);
    setPackagingDialogOrder(null);
    setPackagingUploading(false);

    // ✅ نكمل نفس بروسس زر "تغليف" بعد ما يخلص رفع الفيديو
    sendToBackend(orderId, "تغليف");
  } catch (err) {
    console.error(err);
    alert("❌ حدث خطأ أثناء رفع فيديو التغليف");
    setPackagingUploading(false);
  }
};

const updateDeliveryType = async (orderId, type) => {
  const orderRef = doc(db, "orders", orderId);

  await updateDoc(orderRef, {
    deliveryType: type,
    deliveryPaid: false,
    deliveryPaidBy: null,
    deliveryPaidAt: null,
    deliveryPaidVisibleAt: null,
  });

  setOrders((prev) =>
    prev.map((o) =>
      o.id === orderId
        ? { ...o, deliveryType: type, deliveryPaid: false, deliveryPaidBy: null, deliveryPaidAt: null, deliveryPaidVisibleAt: null }
        : o
    )
  );
};
// ✅ بطلب المستخدم: يستبدل markDeliveryPaid القديم - يكمل الفلو الطبيعي بعد
// اختيار نوع التوصيل بدون تسجيل أي دفع (تسجيل الدفع صار حصراً عبر نظام
// الحسابات عند ربط قيد قبض بالطلب، راجع SmartAccountingSystem.js). نفس حساب
// newStatus/feedbackRequired القديم حرفياً حسب withAssembly.
const continueDeliveryFlow = async (orderId, type) => {
  const orderDoc = orders.find((o) => o.id === orderId);
  const now = Timestamp.now();

  const hasAssembly = !!orderDoc?.withAssembly;

  // - إذا تركيب: يبقى عند برهان حتى يظهر زر تركيب
  // - إذا مو تركيب: مباشرة feedbackRequired
  const newStatus = hasAssembly ? "عند برهان" : "feedbackRequired";
  const feedbackRequired = hasAssembly ? false : true;

  const res = await apiFetch(`${API_BASE}/orders/${orderId}/continue-delivery`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  });
  if (!res.ok) {
    console.error("خطأ أثناء استمرار فلو التوصيل:", await res.text().catch(() => ""));
    return;
  }

  const newHistory = [
    ...(orderDoc.history || []),
    {
      status: `تم تسليم الطلب للتوصيل - ${type === "burhan" ? "الورشة" : "شركة التوصيل"}`,
      by: "برهان",
      timestamp: now,
    },
  ];

  setOrders((prev) =>
    prev.map((o) =>
      o.id === orderId
        ? {
            ...o,
            deliveryConfirmed: true,
            deliveryConfirmedAt: now.toDate(),
            status: newStatus,
            feedbackRequired,
            history: newHistory,
          }
        : o
    )
  );
};


  const searchFiltered = orders.filter((order) =>
    order.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  // ✅ الطلبات المخفية تنحذف من القائمة الرئيسية لغير بنين/المدير، وتنكوم
  // بآخر القائمة لبنين/المدير (تُعرض بقسم منفصل بالأسفل - راجع firstHiddenIndex)
  const visibleFiltered = searchFiltered.filter((o) => !o.hiddenFromBoard);
  const hiddenFiltered = searchFiltered.filter((o) => o.hiddenFromBoard);
  const filtered = canManageHidden
    ? [...visibleFiltered, ...hiddenFiltered]
    : visibleFiltered;
  const firstHiddenIndex = filtered.findIndex((o) => o.hiddenFromBoard);

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h5" gutterBottom>📦 لوحة الورشة</Typography>
<Box display="flex" flexWrap="wrap" gap={2} mb={3}>
  {showBuiltIn("extra:expenses") && (
  <Button
  variant="contained"
  color="secondary"
  sx={{ mb: 2 }}
  onClick={() => navigate("/burhan-expenses")}
>
  💰 مصاريفي
</Button>
  )}
  {showBuiltIn("box:leaveRequest") && (
  <Button
  variant="contained"
  color="secondary"
  sx={{ mb: 2 }}
  onClick={() => navigate("/leave-request?u=art@1234&returnTo=/burhan")}
>
  📝 طلب إجازة
</Button>
  )}
  {showBuiltIn("box:materialRequest") && (
  <Button
  variant="contained"
  color="secondary"
  sx={{ mb: 2 }}
  onClick={() => navigate("/material-request?u=art@1234&returnTo=/burhan")}
>
  🧰 طلب مواد
</Button>
  )}

</Box>




      <TextField
        fullWidth
        label="🔍 ابحث عن اسم الزبون"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      {filtered.map((order, index) => {
           const packagingDone = order.completedSteps?.includes("تغليف");
  const installationDone = order.completedSteps?.includes("تركيب");

  // ✅ بعد التركيب فقط (أو إذا ماكو تركيب: بعد التغليف)
  const readyForDelivery = packagingDone && (!order.withAssembly || installationDone);

  const cardContent = (
<Paper
  key={order.id}
  sx={{
    p: 3,
    my: 2,
    position: "relative",
    borderRadius: 3,
  }}
>

<Box
  sx={{
    position: "absolute",
    top: 15,
    left: 15,
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    gap: 1,
  }}
>
{(() => {
  const pr = getPriorityInfo(order.priority);
  return (
    <Box
      sx={{
        backgroundColor: pr.bg,
        color: "#fff",
        px: 2,
        py: 1,
        borderRadius: "999px",
        display: "flex",
        alignItems: "center",
        gap: 1,
        fontWeight: 800,
        boxShadow: "0 6px 14px rgba(0,0,0,0.2)",
        border: "2px solid rgba(255,255,255,0.2)",
      }}
    >
      <span style={{ letterSpacing: "1px" }}>{pr.label}</span>

      <span
        style={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: pr.dot,
          display: "inline-block",
          boxShadow: "0 0 6px rgba(255,255,255,0.4)",
        }}
      />
    </Box>
  );
})()}

{canManageHidden && (
  <Chip
    size="small"
    clickable
    onClick={() => toggleHiddenFromBoard(order)}
    label={order.hiddenFromBoard ? "👁️ إظهار" : "🚫 إخفاء"}
    sx={{
      fontWeight: 700,
      color: "#fff",
      backgroundColor: order.hiddenFromBoard ? "#2e7d32" : "rgba(0,0,0,0.55)",
      "&:hover": {
        backgroundColor: order.hiddenFromBoard ? "#1b5e20" : "rgba(0,0,0,0.7)",
      },
    }}
  />
)}
</Box>


          <Typography>👤 الزبون: {order.customerName}</Typography>
          <Typography>🌍 المحافظة: {order.governorate}</Typography>
          <Typography>💰 السعر: {Number(order.price).toLocaleString()} دينار</Typography>
          <Typography>📅 التاريخ: {order.createdAt?.toDate().toLocaleString()}</Typography>

          {order.note && (
            <Box mt={2} p={2} bgcolor="#f9f9f9" borderRadius={2}>
              <Typography fontWeight="bold">📝 ملاحظة:</Typography>
              <Typography>{order.note}</Typography>
            </Box>
          )}
          {order.frameNote && (
            <Box mt={2} p={2} bgcolor="#f9f9f9" borderRadius={2}>
              <Typography fontWeight="bold">🖼️ ملاحظات للإطارات:</Typography>
              <Typography>{order.frameNote}</Typography>
            </Box>
          )}
{/* 🛠️ صور التركيب (تظهر فقط إذا الطلب مع تركيب) */}
{order.withAssembly && (
  <Box mt={2} p={2} bgcolor="#fff3e0" borderRadius={2}>
    <Typography fontWeight="bold" sx={{ mb: 1 }}>
      🛠️ صور التركيب:
    </Typography>

    {order.assemblyImages && order.assemblyImages.length > 0 ? (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {order.assemblyImages.map((url, index) => (
          <Box
            key={index}
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #ddd",
              cursor: "pointer",
              background: "#f5f5f5",
            }}
            onClick={() => setPreviewImage(url)}
          >
            <img
              src={url}
              alt={`assembly-${index}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        ))}
      </Box>
    ) : (
      <Typography color="error" fontSize={13}>
        ⚠️ لا توجد صور تركيب مرفوعة لهذا الطلب
      </Typography>
    )}
  </Box>
)}

{/* 🧾 ملف الفاتورة المعتمدة (تجي تلقائياً عند تحويل الفاتورة إلى طلب) */}
{order.invoicePdfUrl ? (
  // 📄 فاتورة فيها ملف PDF كامل (صورة الفاتورة + صفحة تفاصيل وصورة لكل لوحة)
  <Box mt={2} p={2} bgcolor="#e3f2fd" borderRadius={2}>
    <Typography fontWeight="bold" sx={{ mb: 1 }}>
      🧾 فاتورة الطلب (تفاصيل كل لوحة وصورتها):
    </Typography>

    <Button
      variant="contained"
      size="small"
      onClick={() => window.open(order.invoicePdfUrl, "_blank")}
      sx={{ background: "#0d6efd" }}
    >
      📄 فتح ملف الفاتورة PDF
    </Button>
  </Box>
) : (
  order.invoiceImageUrl && (
    // 🧾 فواتير قديمة قبل ميزة الـ PDF - نرجع لعرض الصورة القديمة
    <Box mt={2} p={2} bgcolor="#e3f2fd" borderRadius={2}>
      <Typography fontWeight="bold" sx={{ mb: 1 }}>
        🧾 صورة الفاتورة المعتمدة:
      </Typography>

      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #ddd",
          cursor: "pointer",
          background: "#f5f5f5",
        }}
        onClick={() => setPreviewImage(order.invoiceImageUrl)}
      >
        <img
          src={order.invoiceImageUrl}
          alt="invoice"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      </Box>
    </Box>
  )
)}

{/* 🖼️ صور خاصة برهان (مرفوعة من الموظفة عند إضافة الطلب) */}
{order.burhanImages && order.burhanImages.length > 0 && (
  <Box mt={2} p={2} bgcolor="#ede7f6" borderRadius={2}>
    <Typography fontWeight="bold" sx={{ mb: 1 }}>
      🖼️ صور اللوحات:
    </Typography>

    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      {order.burhanImages.map((url, index) => (
        <Box
          key={index}
          sx={{
            width: 120,
            height: 120,
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #ddd",
            cursor: "pointer",
            background: "#f5f5f5",
          }}
          onClick={() => setPreviewImage(url)}
        >
          <img
            src={url}
            alt={`burhan-${index}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </Box>
      ))}
    </Box>
  </Box>
)}

          <Box mt={2}>
            <Stack direction={isMobile ? "column" : "row"} spacing={1} flexWrap="wrap">
              {getStepsForOrder(order).map((label) => {
                const status = order.status;
 const nextStep = getNextAllowedStep(order);
const isDone = isStepDone(order, label);

// 🔴 اخفي الخطوات اللي بعد الخطوة الجاية
// ✅ استثناء: إذا بعد تأكيد التوصيل لازم يطلع "تركيب" فوراً حتى لو nextStep ما انعكس بعد
const forceShowInstall =
  label === "تركيب" &&
  order.withAssembly &&
  order.completedSteps?.includes("تغليف") &&
  order.deliveryConfirmed === true &&
  !order.completedSteps?.includes("تركيب");

// 🔴 اخفي الخطوات اللي بعد الخطوة الجاية
if (
  !forceShowInstall &&
  !isDone &&
  label !== nextStep &&
  label !== "مشكلة"
) {
  return null;
}


// ✅ تركيب يظهر فقط بعد تأكيد التوصيل إذا الطلب بيه تركيب
if (label === "تركيب" && order.withAssembly && !order.deliveryConfirmed) {
  return null;
}

                // 🚫 إذا الطلب عند حليمة (بانتظار/بدأت) → أخفي كل الأزرار ما عدا "رسم"
                if (
                  (status === "عند برهان - رسم حليمة" ||
                   status === "عند برهان - رسم حليمة بدأت") &&
                  label !== "رسم"
                ) {
                  return null;
                }
// ✅ إذا مكتمل الرسم → خليه يظل ظاهر لكنه يبقى مطفي
if (status === "عند برهان - رسم حليمة مكتمل" && label === "رسم") {
  return (
    <Button
      key={label}
      variant="contained"
      fullWidth={isMobile}
      sx={{
        backgroundColor: "#b0b0b0",
        color: "#fff",
      }}
      disabled
    >
      مكتمل
    </Button>
  );
}


                return (
                  <Button
                    key={label}
                    variant="contained"
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: isStepDone(order, label) ? "#b0b0b0" : "#1976d2",
                      "&:hover": {
                        backgroundColor: isStepDone(order, label) ? "#b0b0b0" : "#1565c0",
                      },
                    }}
disabled={
  (label === "رسم" && order.drawingSent && !isDrawingCompleted(order)) ||
  isStepDone(order, label) ||
  !isBurhanStage(order.status)
}




                 onClick={async () => {
  if (label === "مشكلة") {
    setSelectedOrder(order);
    setOpenDialog(true);
  } 
else if (label === "تغليف") {
  setSelectedPackagingVideos([]);
  setPackagingDialogOrder(order);
}



else if (label === "تركيب") {
  await apiFetch(`${API_BASE}/orders/${order.id}/install`, {
    method: "PUT",
  });

  setOrders((prev) =>
    prev.map((o) =>
      o.id === order.id
        ? {
            ...o,
            completedSteps: [...(o.completedSteps || []), "تركيب"],
            status: "بانتظار تصوير التركيب",
            feedbackRequired: false,
          }
        : o
    )
  );
}
else if (label === "إطار") {
  setSelectedFramingVideos([]);
  setFramingChecklist({ color: false, type: false, size: false, angle: false, hanger: false, finalMeasurements: false });
  setFramingDialogOrder(order);
}
else {
  updateStep(order.id, label);
}
}}
                  >
                    {label === "إطار" ? "بدأ العمل" : label}
                  </Button>
                );
              })}
            </Stack>
            {/* 🚚 اختيار نوع التوصيل بعد التغليف */}
{/* 🚚 أزرار التوصيل – تظهر فقط بعد تغليف */}
{order.completedSteps?.includes("تغليف") && !order.deliveryType && (
  <Stack direction={isMobile ? "column" : "row"} spacing={1} mt={1}>
    <Button
      variant="contained"
      color="info"
     onClick={() => handleDelivery(order.id, "burhan")}
    >
      🚶‍♂️ توصيل – الورشة
    </Button>

    <Button
      variant="contained"
      color="warning"
      onClick={() => handleDelivery(order.id, "company")}
    >
      🚚 شركة التوصيل
    </Button>
  </Stack>
)}


          </Box>
        </Paper>
       );

  // ✅ طلب مخفي وما عندك صلاحية (غير بنين/المدير) - ما يظهر إطلاقاً
  if (order.hiddenFromBoard && !canManageHidden) return null;

  // ✅ طلب مخفي وعندك صلاحية - يظهر بقسم "الطلبات المخفية" بالأسفل كبطاقة
  // قابلة للطي، وبالضغط عليها يطلع نفس محتوى الطلب بالضبط (نفس cardContent
  // فوق) بكل أزراره ووظائفه - الإخفاء بصري فقط، الطلب يشتغل بنفس الطريقة تماماً
  if (order.hiddenFromBoard) {
    return (
      <React.Fragment key={order.id}>
        {index === firstHiddenIndex && (
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 4, mb: 1 }}>
            🚫 الطلبات المخفية ({hiddenFiltered.length})
          </Typography>
        )}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">
              {order.customerName} — {getDisplayStatus(order.status, order)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>{cardContent}</AccordionDetails>
        </Accordion>
      </React.Fragment>
    );
  }

  return cardContent;
})}
{/* 🖼️ Popup لتكبير الصور */}
<Dialog
  open={!!previewImage}
  onClose={() => setPreviewImage(null)}
  maxWidth="md"
  fullWidth
>
  <Box
    sx={{
      maxHeight: "90vh",
      overflowY: "auto",
      p: 2,
      display: "flex",
      justifyContent: "center",
      backgroundColor: "#000",
    }}
  >
    <img
      src={previewImage}
      alt="preview"
      style={{
        width: "100%",
        height: "auto",
        objectFit: "contain",
      }}
    />
  </Box>
</Dialog>

      {/* 🎥 رفع فيديو التأطير - إلزامي قبل إكمال خطوة "إطار" */}
      <Dialog open={!!framingDialogOrder} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>🎥 رفع فيديو التأطير (إلزامي)</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            يجب رفع فيديو واحد أو أكثر لعملية التأطير قبل إكمال هذه الخطوة والمتابعة.
          </Typography>

          <Box sx={{ mb: 2, p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>
              ✅ نقاط الفحص (إلزامية)
            </Typography>
            {FRAMING_CHECKLIST_ITEMS.map((item) => (
              <FormControlLabel
                key={item.key}
                sx={{ display: "flex" }}
                control={
                  <Checkbox
                    checked={framingChecklist[item.key]}
                    onChange={(e) =>
                      setFramingChecklist((prev) => ({
                        ...prev,
                        [item.key]: e.target.checked,
                      }))
                    }
                  />
                }
                label={item.label}
              />
            ))}
          </Box>

          <Button variant="outlined" component="label" fullWidth>
            📤 اختيار فيديو / فيديوهات التأطير
            <input
              hidden
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => appendPickedFiles(e, setSelectedFramingVideos)}
            />
          </Button>

          {selectedFramingVideos.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: "bold" }}>🎥 الملفات المختارة:</Typography>
              <UploadPreview files={selectedFramingVideos} onChange={setSelectedFramingVideos} />
            </Box>
          )}

          {framingUploading && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <CircularProgress size={22} />
              <Typography sx={{ mt: 1 }}>جاري رفع الفيديو...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={framingUploading}
            onClick={() => {
              setFramingDialogOrder(null);
              setSelectedFramingVideos([]);
              setFramingChecklist({ color: false, type: false, size: false, angle: false, hanger: false, finalMeasurements: false });
            }}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={framingUploading}
            onClick={handleUploadFramingVideos}
          >
            ✅ رفع ومتابعة
          </Button>
        </DialogActions>
      </Dialog>

      {/* 🎥 رفع فيديو التغليف - إلزامي قبل إكمال خطوة "تغليف" */}
      <Dialog open={!!packagingDialogOrder} onClose={() => {}} maxWidth="sm" fullWidth>
        <DialogTitle>🎥 رفع فيديو التغليف (إلزامي)</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            يجب رفع فيديو واحد أو أكثر لعملية التغليف قبل إكمال هذه الخطوة والمتابعة.
          </Typography>

          <Button variant="outlined" component="label" fullWidth>
            📤 اختيار فيديو / فيديوهات التغليف
            <input
              hidden
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => appendPickedFiles(e, setSelectedPackagingVideos)}
            />
          </Button>

          {selectedPackagingVideos.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontWeight: "bold" }}>🎥 الملفات المختارة:</Typography>
              <UploadPreview files={selectedPackagingVideos} onChange={setSelectedPackagingVideos} />
            </Box>
          )}

          {packagingUploading && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <CircularProgress size={22} />
              <Typography sx={{ mt: 1 }}>جاري رفع الفيديو...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={packagingUploading}
            onClick={() => {
              setPackagingDialogOrder(null);
              setSelectedPackagingVideos([]);
            }}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={packagingUploading}
            onClick={handleUploadPackagingVideos}
          >
            ✅ رفع ومتابعة
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>🔧 أرسل الطلب كمشكلة</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="تفاصيل المشكلة"
            fullWidth
            multiline
            value={problemNote}
            onChange={(e) => setProblemNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>إلغاء</Button>
          <Button onClick={() => updateStep(selectedOrder.id, "مشكلة")}>
  إرسال
</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BurhanDashboard;
